import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getHeaders } from '../lib/useQobuz';
import { CLOUD_API_URL } from '../config/serverConfig';
import { getQualityTier, qualifiesHiResAudioBadge } from '../hooks/useAudioQuality';
import HiResAudioBadge from './HiResAudioBadge';
import { prefersFinePointerHover } from '../utils/touchUi';

export const CARD_SIZE = 240;

function getCover(a) {
  if (!a) return null;
  if (a.image?.large) return a.image.large;
  if (a.image?.small) return a.image.small;
  if (a.image?.thumbnail) return a.image.thumbnail;
  if (typeof a.image === 'string') return a.image;
  return a.cover || a.coverUrl || a.cover_url || a.coverUrlLarge || a.cover_url_large || a.artworkUrl || a.cover_art_url || null;
}

function formatTotalDuration(seconds) {
  if (!seconds) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m} min`;
}

const albumDescCache = new Map();
async function fetchAlbumDescription(albumId) {
  const rawId = String(albumId || '').replace(/^qobuz:(album|track|artist):/, '');
  if (!rawId) return null;
  if (albumDescCache.has(rawId)) return albumDescCache.get(rawId);
  try {
    const r = await fetch(`${CLOUD_API_URL}/api/streaming/qobuz/album-detail?album_id=${rawId}`, { headers: getHeaders() });
    if (!r.ok) { albumDescCache.set(rawId, null); return null; }
    const data = await r.json();
    let desc = data?.description || data?.catchline || null;
    if (desc) desc = desc.replace(/<[^>]+>/g, '').trim();
    albumDescCache.set(rawId, desc || null);
    return desc || null;
  } catch {
    albumDescCache.set(rawId, null);
    return null;
  }
}

export default function AlbumCoverCard({ album, onClick, animDelay = 0 }) {
  const [hovered, setHovered] = useState(false);
  const [description, setDescription] = useState(undefined);
  const fetchTimerRef = useRef(null);

  const cover = getCover(album);
  const title = album.title || '';
  const artist = album.artist?.name || album.artistName || album.artist_name || '';
  const genre = typeof album.genre === 'object' ? album.genre?.name : (album.genre || '');
  const label = typeof album.label === 'object' ? album.label?.name : (album.label || '');
  const trackCount = album.tracks_count || album.trackCount || 0;
  const maxBitDepth = album.maximum_bit_depth || album.maxBitDepth || null;
  const maxSampleRate = album.maximum_sampling_rate || album.maxSampleRate || null;
  const tier = album.qualityTier || getQualityTier(maxBitDepth, maxSampleRate);
  const hires = tier?.tier === 'hi-res' || tier?.tier === 'med-res';
  const duration = album.duration || 0;
  const releaseDate = album.release_date_original || album.releaseDate || album.releasedAt;

  let year = '';
  if (releaseDate) {
    const parsed = typeof releaseDate === 'number' && releaseDate > 1e9
      ? new Date(releaseDate * 1000).getFullYear()
      : new Date(releaseDate).getFullYear();
    if (!isNaN(parsed)) year = String(parsed);
  }

  const metaParts = [];
  if (year) metaParts.push(year);
  if (genre) metaParts.push(genre);
  if (label) metaParts.push(label);
  const metaLine = metaParts.join(' · ');

  const qualityParts = [];
  if (trackCount > 0) qualityParts.push(`${trackCount} tracks`);
  if (duration > 0) qualityParts.push(formatTotalDuration(duration));
  if (hires && maxBitDepth && maxSampleRate) qualityParts.push(`${maxBitDepth}-bit / ${maxSampleRate}kHz`);
  const qualityLine = qualityParts.join(' · ');

  const fallbackDesc = useMemo(() => {
    const parts = [];
    if (artist && genre) parts.push(`A${/^[aeiou]/i.test(genre) ? 'n' : ''} ${genre.toLowerCase()} album by ${artist}.`);
    else if (artist) parts.push(`An album by ${artist}.`);
    if (label && year) parts.push(`Released in ${year} on ${label}.`);
    else if (year) parts.push(`Released in ${year}.`);
    else if (label) parts.push(`On ${label}.`);
    if (trackCount > 0 && duration > 0) parts.push(`${trackCount} tracks, ${formatTotalDuration(duration)}.`);
    if (hires && maxBitDepth && maxSampleRate) parts.push(`Available in ${tier.label} at ${maxBitDepth}-bit/${maxSampleRate}kHz.`);
    return parts.join(' ');
  }, [artist, genre, label, year, trackCount, duration, hires, tier, maxBitDepth, maxSampleRate]);

  const handleMouseEnter = useCallback(() => {
    if (!prefersFinePointerHover()) return;
    setHovered(true);
    if (description === undefined) {
      fetchTimerRef.current = setTimeout(() => {
        const albumId = album.serviceAlbumId || album.id;
        fetchAlbumDescription(albumId).then(setDescription);
      }, 150);
    }
  }, [album, description]);

  const handleMouseLeave = useCallback(() => {
    setHovered(false);
    if (fetchTimerRef.current) {
      clearTimeout(fetchTimerRef.current);
      fetchTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
  }, []);

  const displayDesc = description || fallbackDesc;

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex-shrink-0 text-left group touch-manipulation select-none [-webkit-tap-highlight-color:transparent]"
      style={{ width: CARD_SIZE, animation: `homeFadeIn 0.4s ease ${animDelay}ms both` }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className="rounded-xl overflow-hidden"
        style={{
          width: CARD_SIZE, height: CARD_SIZE,
          background: '#fff',
          boxShadow: hovered ? '0 14px 44px rgba(0,0,0,0.24)' : '0 4px 18px rgba(0,0,0,0.10)',
          border: '1px solid rgba(0,0,0,0.06)',
          position: 'relative',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          transform: hovered ? 'scale(1.04)' : 'scale(1)',
        }}
      >
        {cover ? (
          <img src={cover} alt={title} className="w-full h-full object-cover pointer-events-none" loading="lazy" draggable={false} />
        ) : (
          <div className="w-full h-full flex items-center justify-center pointer-events-none" style={{ background: '#f5f5f5', color: '#ccc', fontSize: 48 }}>♪</div>
        )}
        {!hovered && qualifiesHiResAudioBadge(maxBitDepth, maxSampleRate) && (
          <div className="pointer-events-none" style={{ position: 'absolute', bottom: 8, left: 8, zIndex: 3 }}>
            <HiResAudioBadge bitDepth={maxBitDepth} sampleRate={maxSampleRate} height={16} />
          </div>
        )}
        {!hovered && !qualifiesHiResAudioBadge(maxBitDepth, maxSampleRate) && hires && tier && (
          <span className="pointer-events-none" style={{
            position: 'absolute', bottom: 8, left: 8, zIndex: 3,
            fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 6,
            background: tier.solidBg, color: tier.solidColor, boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
          }}>
            {tier.label}
          </span>
        )}

        {/* Rich description overlay on hover */}
        <div
          className="pointer-events-none"
          style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to bottom, transparent 8%, rgba(0,0,0,0.92) 42%)',
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
            padding: '16px 16px 14px',
            color: '#fff',
            opacity: hovered ? 1 : 0,
            transition: 'opacity 0.25s ease',
            zIndex: 2,
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.25, marginBottom: 3 }}>{title}</div>
          <div style={{ fontSize: 12, fontWeight: 500, opacity: 0.85, marginBottom: 6 }}>{artist}</div>

          {metaLine && (
            <div style={{ fontSize: 10, opacity: 0.6, marginBottom: 3 }}>{metaLine}</div>
          )}
          {qualityLine && (
            <div style={{ fontSize: 10, opacity: 0.55, display: 'flex', alignItems: 'center', gap: 4 }}>
              {qualifiesHiResAudioBadge(maxBitDepth, maxSampleRate) ? (
                <HiResAudioBadge bitDepth={maxBitDepth} sampleRate={maxSampleRate} height={16} />
              ) : hires && tier ? (
                <span style={{
                  background: tier.solidBg, color: tier.solidColor, fontSize: 8, fontWeight: 800,
                  padding: '1px 5px', borderRadius: 4, lineHeight: '14px',
                }}>{tier.label}</span>
              ) : null}
              <span>{qualityLine}</span>
            </div>
          )}
        </div>
      </div>

      {/* Title + artist always visible below */}
      <div style={{ marginTop: 10, paddingRight: 4 }}>
        <div style={{
          fontSize: 14, fontWeight: 700, color: 'var(--text-primary)',
          overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: '1.3',
        }}>
          {title}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {artist}
        </div>
      </div>
    </button>
  );
}
